/**
 * ek-status-bar (EK-043)
 *
 * Every pack state is already in the markup (all nodes and labels, one
 * message per pack), so a pack switch is a class toggle. The element listens
 * for the pack radio `change` in its own section and updates in the same
 * frame as the pack card, without waiting for the theme's variant fetch.
 *
 * The fetched section HTML (`variant:changed`) is used only to reconcile:
 * if the server-rendered bar disagrees with the current state (for example a
 * change driven by something other than the radios), the element is replaced.
 */
(() => {
  if (customElements.get('ek-status-bar')) return;

  class EkStatusBar extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.sectionId = this.dataset.sectionId;
      this.packOption = this.dataset.packOption;

      this.onChange = (event) => {
        const input = event.target;
        if (!input || input.type !== 'radio' || !input.checked) return;
        if (!this.packOption) return;
        if (input.name !== this.packOption && input.name !== `${this.packOption}-${this.sectionId}`) return;

        const label = this.querySelector(`.ek-sb__label[data-value="${CSS.escape(input.value.trim())}"]`);
        if (!label) return;
        this.setStep(parseInt(label.dataset.step, 10));
      };
      document.addEventListener('change', this.onChange);

      if (this.productId && this.sectionId && typeof subscribe === 'function') {
        this.unsubscribe = subscribe(`variant:changed:${this.productId}:${this.sectionId}`, ({ html }) => {
          const next = html?.querySelector('ek-status-bar');
          if (!next) return;
          if (next.dataset.selected === this.dataset.selected) return;

          this.replaceWith(next.cloneNode(true));
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onChange);
      this.unsubscribe?.();
    }

    setStep(step) {
      const steps = parseInt(this.dataset.steps, 10) || 1;
      if (!step || step < 1 || step > steps) return;
      if (String(step) === this.dataset.selected) return;

      this.dataset.selected = String(step);
      this.style.setProperty('--ek-sb-fill', `${steps > 1 ? ((step - 1) * 100) / (steps - 1) : 0}%`);

      this.querySelectorAll('.ek-sb__node').forEach((node) => {
        node.classList.toggle('ek-sb__node--done', parseInt(node.dataset.step, 10) <= step);
      });

      this.querySelectorAll('.ek-sb__label').forEach((label) => {
        const own = parseInt(label.dataset.step, 10);
        label.classList.toggle('ek-sb__label--done', own <= step);
        label.classList.toggle('ek-sb__label--current', own === step);
        if (own === step) {
          label.setAttribute('aria-current', 'step');
        } else {
          label.removeAttribute('aria-current');
        }
      });

      this.querySelectorAll('.ek-sb__message').forEach((message) => {
        message.classList.toggle('ek-sb__message--active', parseInt(message.dataset.step, 10) === step);
      });
    }
  }

  customElements.define('ek-status-bar', EkStatusBar);
})();
